import { useState, useCallback } from 'react';

import { useTranslate, useNotify } from 'react-admin';

import { useAgentOperations } from '../utils/agentOperations';

import type { AgentSettings, Queue } from '@ragu-components/ord-types';
import type { Identifier } from 'react-admin';

interface AgentPairs {
  users: Map<string, Identifier>;
  groups: Map<string, Identifier>;
}

interface UseAgentsOperationsParams {
  record: Queue;
  onAgentsUpdateEvent: () => void;
  onUserDeleteFailed?: (failedUserIds: string[]) => void;
  onGroupDeleteFailed?: (failedGroupIds: string[]) => void;
  onUserCreateFailed?: (failedUserIds: string[]) => void;
  onGroupCreateFailed?: (failedGroupIds: string[]) => void;
}

interface UseAgentsOperationsReturn {
  updateAgents: (
    userIds: string[],
    groupIds: string[],
    agentPairs: AgentPairs,
    labelIds: string[],
    agentSettings: AgentSettings
  ) => Promise<void>;
  updateDisabled: boolean;
  setUpdateDisabled: (disabled: boolean) => void;
}

export const useAgentsOperations = ({
  record,
  onAgentsUpdateEvent,
  onUserDeleteFailed,
  onGroupDeleteFailed,
  onUserCreateFailed,
  onGroupCreateFailed,
}: UseAgentsOperationsParams): UseAgentsOperationsReturn => {
  const [updateDisabled, setUpdateDisabled] = useState<boolean>(true);
  const translate = useTranslate();
  const notify = useNotify();

  const { deleteAgents, createGroupAgents, createUserAgents } = useAgentOperations();

  const notifyResults = useCallback(
    (totalAttempted: number, totalFailed: number, errors: Set<string>) => {
      const totalSuccessful = totalAttempted - totalFailed;

      if (totalFailed > 0) {
        const errorsMessage = errors.size > 0 ? ` (${Array.from(errors).join(', ')})` : '';
        const updateManyFailedMessage = `${translate('ord.httpErrors.updateManyFailed')} ${errorsMessage}`;

        if (totalSuccessful > 0) {
          const updatedMessage = translate('ra.notification.updated', { smart_count: totalSuccessful });
          const message = `${updatedMessage}. ${updateManyFailedMessage}`;
          notify(message, {
            type: 'warning',
            messageArgs: { _: message },
          });
        } else {
          notify(updateManyFailedMessage, {
            type: 'error',
            messageArgs: { _: updateManyFailedMessage },
          });
        }
      } else {
        notify('ra.notification.updated', {
          type: 'success',
          messageArgs: { smart_count: totalSuccessful },
        });
      }
    },
    [translate, notify]
  );

  const updateAgents = useCallback(
    async (
      userIds: string[],
      groupIds: string[],
      agentPairs: AgentPairs,
      labelIds: string[],
      agentSettings: AgentSettings
    ): Promise<void> => {
      setUpdateDisabled(true);

      const allErrors = new Set<string>();

      // Delete operations
      const userDeleteResult = await deleteAgents(agentPairs.users, userIds);
      const groupDeleteResult = await deleteAgents(agentPairs.groups, groupIds);

      // Create operations
      const userCreateResult = await createUserAgents(userIds, agentPairs.users, record, labelIds, agentSettings);
      const groupCreateResult = await createGroupAgents(groupIds, agentPairs.groups, record, labelIds, agentSettings);

      // Collect all errors
      userDeleteResult.errors.forEach((error) => allErrors.add(error));
      groupDeleteResult.errors.forEach((error) => allErrors.add(error));
      userCreateResult.errors.forEach((error) => allErrors.add(error));
      groupCreateResult.errors.forEach((error) => allErrors.add(error));

      // Calculate totals
      const totalAttempted =
        userDeleteResult.successful.length +
        userDeleteResult.failed.length +
        groupDeleteResult.successful.length +
        groupDeleteResult.failed.length +
        userCreateResult.successful.length +
        userCreateResult.failed.length +
        groupCreateResult.successful.length +
        groupCreateResult.failed.length;

      const totalFailed =
        userDeleteResult.failed.length +
        groupDeleteResult.failed.length +
        userCreateResult.failed.length +
        groupCreateResult.failed.length;

      // For deletion failures: entities should stay in the list
      // For creation failures: entities should be removed list

      if (userDeleteResult.failed.length > 0 && onUserDeleteFailed) {
        onUserDeleteFailed(userDeleteResult.failed);
      }

      if (groupDeleteResult.failed.length > 0 && onGroupDeleteFailed) {
        onGroupDeleteFailed(groupDeleteResult.failed);
      }

      if (userCreateResult.failed.length > 0 && onUserCreateFailed) {
        onUserCreateFailed(userCreateResult.failed);
      }

      if (groupCreateResult.failed.length > 0 && onGroupCreateFailed) {
        onGroupCreateFailed(groupCreateResult.failed);
      }

      notifyResults(totalAttempted, totalFailed, allErrors);
      onAgentsUpdateEvent();
    },
    [
      deleteAgents,
      createUserAgents,
      createGroupAgents,
      record,
      notifyResults,
      onAgentsUpdateEvent,
      onUserDeleteFailed,
      onGroupDeleteFailed,
      onUserCreateFailed,
      onGroupCreateFailed,
    ]
  );

  return {
    updateAgents,
    updateDisabled,
    setUpdateDisabled,
  };
};
